/**
 * The one description of where the reel is, shared by the WebGL scene that
 * draws it and the DOM film that has to sit on its rim.
 *
 * The film is HTML and the reel is geometry, so they are projected by two
 * completely separate systems: three.js's perspective camera, and CSS's
 * `perspective` property. Left to guesswork those two disagree, and the film
 * ends up floating near the wheel rather than wound onto it — which is what a
 * hardcoded pixel radius was doing before.
 *
 * They can be made to agree exactly, because CSS `perspective` is the same kind
 * of projection: a viewer at a fixed distance from the z = 0 plane. Match that
 * distance to the camera's, convert the reel's radius from world units into
 * pixels at that plane, and the DOM cylinder lands on the rendered rim at any
 * viewport size.
 */

/** Camera distance from the origin, in world units. Mirrors the Canvas prop. */
export const CAMERA_DISTANCE = 5;

/** Vertical field of view, in degrees. Mirrors the Canvas prop. */
export const CAMERA_FOV = 42;

/** Radius of the reel's outer rim, in world units. */
export const REEL_RADIUS = 1.5;

/** Depth the reel plate is extruded to, in world units. Matches ReelMesh. */
const PLATE_DEPTH = 0.14;

/**
 * Widest a frame of film is ever drawn, in pixels.
 *
 * This is also the drum's thickness — the film is wound *across* the reel, so
 * the two are the same measurement seen from different sides. Kept here rather
 * than in ReelScroller so `drumThicknessScale` below can derive from it and the
 * two can never disagree.
 *
 * Narrower than it used to be, and that is what fixes the spool proportions: a
 * reel with film on it is much wider across than it is thick, and at 520px
 * against a 698px diameter this read as a barrel — 1.34 to 1 — rather than as
 * a reel.
 */
export const FRAME_WIDTH_MAX = 430;

/**
 * Viewport width at which the page stops stacking the hero.
 *
 * The same 1024px Tailwind calls `lg`, and deliberately the same number the
 * hero's own grid switches at — below it the hero is one column with the reel
 * under the copy, above it the reel has a column of its own. Everything on this
 * side of the breakpoint is a *drawing* decision, not a capability one: whether
 * the machine can run the scene at all is `canRenderStage`, which no longer
 * looks at width for the reasons set out there.
 */
export const COMPACT_BREAKPOINT = 1024;

/**
 * Margin left either side of the film, in pixels.
 *
 * The page gutter is 20px (`px-5`) and the film should not run right up to it,
 * so this is a little more than twice that.
 */
const FRAME_MARGIN = 48;

/**
 * How much of a narrow viewport the film is allowed to take.
 *
 * The drum's width *is* the film's width — they are the same measurement — so
 * this also decides how much of the wheel's silhouette is left on screen. Film
 * clamped to the viewport with a 24px margin was arithmetically fine and looked
 * wrong: at 88% of a phone's width the drum's curved sides are off both edges,
 * and what is left is a flat gold wall with pictures on it rather than a wheel
 * with film wound round it. The edges are the whole tell.
 */
const COMPACT_FRAME_FRACTION = 0.72;

/**
 * The most of the drum's diameter the film is allowed to be thick.
 *
 * A reel is much wider across than it is thick, and this is the one number that
 * holds that true at every viewport. Taken from the wide layout, which sits at
 * about 0.53 at a typical window and has never exceeded this.
 */
const FRAME_TO_DIAMETER = 0.62;

/**
 * Width of one frame of film, for a given viewport.
 *
 * Fixed at 430px this was wider than every phone made — a 393px handset got a
 * 430px drum, so the film hung 18px off each side of the screen and the
 * sprocket rails, which are the part that says "film", were the first thing
 * over the edge. Clamping to the viewport keeps the whole strip on screen at
 * any size while leaving the desktop measurement exactly where it was.
 */
export function frameWidth(
  viewportWidth: number,
  viewportHeight: number,
): number {
  const byViewport =
    viewportWidth < COMPACT_BREAKPOINT
      ? viewportWidth * COMPACT_FRAME_FRACTION
      : viewportWidth - FRAME_MARGIN;

  /*
   * The drum's own size, which is the bound that actually matters.
   *
   * Fitting the film to the viewport's *width* alone is only right while the
   * viewport is a tall thin phone. Turn that phone on its side and the wheel —
   * whose radius comes from viewport height — collapses to a 111px radius while
   * 72% of an 852px width asks for a 613px frame: a drum nearly three times as
   * thick as it is round, which is a tube. The same thing happened on an iPad
   * in portrait and in any browser window under 1024px wide.
   *
   * A reel is a fixed proportion, so that is what this enforces. 0.62 of the
   * diameter is what the wide layout has always drawn at a normal window size,
   * where this bound does not bind and nothing changes.
   */
  const diameter =
    2 * REEL_RADIUS * stageScale(viewportWidth) * pixelsPerWorldUnit(viewportHeight);

  return Math.round(
    Math.min(FRAME_WIDTH_MAX, byViewport, diameter * FRAME_TO_DIAMETER),
  );
}

/**
 * Scale the reel settles at once it has turned onto its rim.
 *
 * This is what sets how tall each frame of film can be. The frames are wound on
 * the rim, so the arc available to each one is the rim's circumference over the
 * number of cells — a bigger wheel is the only way to give every review more
 * height without either overlapping its neighbours or unwinding the film from
 * the reel it is supposed to be on.
 */
export const STAGE_SCALE = 1.15;

/**
 * The same, on a narrow screen.
 *
 * A smaller wheel, and deliberately so. Every measurement that matters here is
 * a *ratio*, and at 1.15 on a phone they all came out wrong at once: the rim's
 * radius is derived from viewport height, so a portrait handset got a wheel
 * 766px across on a 393px-wide screen. The drum's curvature ran off the top and
 * bottom of the film area, and — because each frame's height is the rim's
 * circumference divided between the cells — the frames came out taller than
 * they were wide. Square frames crop a film still to a portrait letterbox,
 * which is the one shape a film still is never composed for.
 *
 * At 0.72 the whole wheel fits the film area with its curve showing top and
 * bottom, and the frames land at about 1.7:1 — the same landscape the wide
 * layout has always drawn.
 */
export const COMPACT_STAGE_SCALE = 0.72;

/** Whichever of the two this viewport gets. */
export function stageScale(viewportWidth: number): number {
  return viewportWidth < COMPACT_BREAKPOINT ? COMPACT_STAGE_SCALE : STAGE_SCALE;
}

/** Where the reel sits on the stage, in world units. */
export const STAGE_POSITION: readonly [number, number] = [0, 0.1];

/** Where it starts, over the hero's right-hand column. */
export const HERO_POSITION: readonly [number, number] = [1.62, 0.15];
export const HERO_SCALE = 0.56;

/**
 * Pixels per world unit at the z = 0 plane.
 *
 * A perspective camera sees a window `2 · d · tan(fov / 2)` tall in world units
 * at the plane it is focused on. Dividing the viewport's pixel height by that
 * gives the scale factor between the two coordinate systems.
 */
export function pixelsPerWorldUnit(viewportHeight: number): number {
  const visibleHeight =
    2 * CAMERA_DISTANCE * Math.tan((CAMERA_FOV * Math.PI) / 360);

  return viewportHeight / visibleHeight;
}

/**
 * How far to stretch the reel along its own axle so the drum is exactly as
 * thick as the film is wide.
 *
 * The model is a plate 0.14 deep — right for an empty reel seen face-on, and
 * far too thin the moment it turns edge-on and has film wound across it. This
 * used to be a hardcoded multiplier, which meant every change to the frame
 * width silently pulled the film off the edge of the drum it is supposed to sit
 * on. Deriving it from the frame width makes that impossible.
 *
 * `STAGE_SCALE` is divided back out because the group this stretch is applied
 * to is *inside* the one the stage scale is applied to, so the two multiply.
 * Without it the drum came out 15% thicker than the film wound on it was wide,
 * which is a rim of bare metal down both edges of the tape.
 */
export function drumThicknessScale(
  viewportWidth: number,
  viewportHeight: number,
): number {
  const worldThickness =
    frameWidth(viewportWidth, viewportHeight) /
    pixelsPerWorldUnit(viewportHeight);

  return worldThickness / (PLATE_DEPTH * stageScale(viewportWidth));
}

/**
 * Marks the element the hero reserves for the reel.
 *
 * The reel is drawn on a canvas fixed to the whole viewport, so it has no idea
 * where the hero put its slot — on a wide screen that is a column on the right,
 * and on a phone the hero stacks and it is a square under the copy. Rather than
 * keep one hardcoded world position per layout and re-derive it every time the
 * hero changes, the slot says where it is and the reel reads it.
 */
export const REEL_SLOT_ATTR = "data-reel-slot";

/** Where the reel sits, and how big, while the hero owns it. */
export interface HeroPlacement {
  x: number;
  y: number;
  scale: number;
}

/**
 * Where the reel should sit while the hero owns it, in world units.
 *
 * Measured from the hero's own slot when there is one on the page, which is
 * what makes this work on a stacked layout: the hardcoded `HERO_POSITION`
 * below is 1.62 world units to the *right* of centre, and on a 393px-wide
 * phone that is roughly 360px right of a 196px half-width — the entire reel
 * off the side of the screen. The slot is wherever the hero actually put it,
 * at whatever width it actually is, so reading it is right at every size.
 *
 * The constants remain the fallback for the moment before the hero has laid
 * out, and for the wide layout they were measured on.
 */
export function heroPlacement(
  viewportWidth: number,
  viewportHeight: number,
): HeroPlacement {
  /*
   * The wide layout's measurements, for before the hero has laid out.
   *
   * Narrow viewports get a centred fallback rather than this one, and that is
   * not a nicety: `HERO_POSITION` is 1.62 world units right of centre because
   * that is where the two-column hero's right-hand column is. Handed to a
   * stacked layout it puts the reel entirely off the side of the screen — a
   * sliver of gold rim at the edge and nothing else — which is a far worse
   * failure than a reel sitting somewhere approximate.
   */
  const compact = viewportWidth < COMPACT_BREAKPOINT;
  const fallback: HeroPlacement = compact
    ? { x: 0, y: -0.35, scale: 0.4 }
    : { x: HERO_POSITION[0], y: HERO_POSITION[1], scale: HERO_SCALE };

  if (typeof document === "undefined") return fallback;

  const slot = document.querySelector<HTMLElement>(`[${REEL_SLOT_ATTR}]`);
  if (!slot) return fallback;

  const rect = slot.getBoundingClientRect();
  if (rect.width === 0 || rect.height === 0) return fallback;

  const scale = pixelsPerWorldUnit(viewportHeight);

  return {
    // Screen x grows rightward like world x, so this is a straight offset from
    // the viewport's centre.
    x: (rect.left + rect.width / 2 - viewportWidth / 2) / scale,
    // Screen y grows downward and world y grows upward, hence the negation.
    y: -(rect.top + rect.height / 2 - viewportHeight / 2) / scale,
    // The slot is square, but `min` rather than either edge so a slot that is
    // ever not square still fits the reel inside it rather than overflowing.
    scale: Math.min(rect.width, rect.height) / (2 * REEL_RADIUS * scale),
  };
}

export interface ProjectedReel {
  /** Radius of the rim as rendered, in pixels. */
  radius: number;
  /**
   * CSS `perspective` that matches the camera, in pixels.
   *
   * Same viewer distance expressed in the DOM's units, so a CSS transform of
   * `translateZ(radius)` foreshortens by exactly as much as the equivalent
   * point on the WebGL rim does.
   */
  perspective: number;
  /** How far below the viewport's centre the reel's axle sits, in pixels. */
  centreOffsetY: number;
}

/**
 * Where the reel actually is on screen, for a given viewport.
 *
 * The canvas is fixed to the whole viewport, so the reel's axle is at the
 * viewport's centre plus its own world-space offset. The film's container is
 * somewhere else in the layout entirely, so it needs that difference to line
 * its cylinder up with the axle rather than with itself.
 */
export function projectReel(
  viewportWidth: number,
  viewportHeight: number,
): ProjectedReel {
  const scale = pixelsPerWorldUnit(viewportHeight);

  return {
    radius: REEL_RADIUS * stageScale(viewportWidth) * scale,
    perspective: CAMERA_DISTANCE * scale,
    // Screen y grows downward and world y grows upward, hence the negation.
    centreOffsetY: -STAGE_POSITION[1] * scale,
  };
}
